/**
 * Business Controls → Notifications (Trera and Traci, 27 Sep 2026): every email and text clients and talent get,
 * with an on/off switch, a Preview of each as it goes out, the wording to edit, a live preview while editing,
 * change history, and a test send to any inbox.
 * Templates and the renderer: supabase/functions/_shared/notificationTemplates.js (the same file the senders use,
 * so the preview is exactly what goes out). API: /api/admin/staging/notifications/templates (Admin only).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Eye, History, Loader2, Mail, MessageSquare, MessagesSquare, Pencil, RotateCcw, Send } from 'lucide-react';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Textarea } from '@/components/admin/ui/textarea';
import { Switch } from '@/components/admin/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { catalogEntry, checkTemplate, renderChat, renderEmail, renderSms, sampleValues } from '../../../supabase/functions/_shared/notificationTemplates.js';
import { stagingFetch } from '../api';

type Field = { name: string; label: string; sample: string; optional?: boolean };
type Item = {
  key: string; name: string; channel: 'email' | 'sms' | 'chat'; audience: string; when: string; offer: boolean; enabled: boolean;
  customized: boolean; subject: string | null; body: string; default_subject: string | null; default_body: string;
  fields: Field[]; updated_at: string | null; updated_by_name: string | null; sent_30d: number | null; sent_note: string | null;
};
type Group = { group: string; label: string; sections: { section: string; items: Item[] }[] };

const API = '/api/admin/staging/notifications/templates';
const shortDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const ACTION_LABEL: Record<string, string> = { edited: 'Wording changed', turned_on: 'Turned on', turned_off: 'Turned off', reset: 'Back to original' };
// Portal chat messages (2 Nov 2026) sit beside the emails and texts
const CHANNEL_LABEL: Record<string, string> = { email: 'Email', sms: 'Text', chat: 'Portal chat' };

function Preview({ item, subject, body }: { item: Item; subject: string; body: string }) {
  const base = catalogEntry(item.key);
  const values = sampleValues(base);
  if (item.channel === 'chat') {
    const text = renderChat({ ...base, body }, values);
    return (
      <div className="rounded-lg border bg-muted/30 p-4">
        <p className="text-xs text-muted-foreground mb-2">Love &amp; Photos Team</p>
        <p className="max-w-md whitespace-pre-line rounded-2xl rounded-tl-sm bg-card border px-4 py-3 text-sm leading-snug">{text}</p>
        <p className="mt-2 text-xs text-muted-foreground">Shows in the {item.audience.toLowerCase().startsWith('client') ? "client's" : "talent's"} portal chat.</p>
      </div>
    );
  }
  if (item.channel === 'sms') {
    const text = renderSms({ ...base, body }, values);
    return (
      <div className="rounded-lg border bg-muted/30 p-4">
        <p className="max-w-sm rounded-2xl bg-[#e9e9eb] px-4 py-3 text-sm leading-snug">{text}</p>
        <p className="mt-2 text-xs text-muted-foreground">{text.length} characters. &ldquo;Reply STOP to opt out.&rdquo; is always added.</p>
      </div>
    );
  }
  const email = renderEmail({ ...base, subject, body }, values);
  return (
    <div className="rounded-lg border">
      <div className="border-b bg-muted/40 px-3 py-2 text-xs"><span className="text-muted-foreground">Subject: </span><b>{email.subject}</b></div>
      <iframe title="Email preview" className="h-[460px] w-full bg-white" srcDoc={email.html} sandbox="" />
    </div>
  );
}

function SendTest({ itemKey, subject, body, defaultTo, compact = false }: { itemKey: string; subject?: string; body?: string; defaultTo: string; compact?: boolean }) {
  const [to, setTo] = useState(defaultTo);
  const [sending, setSending] = useState(false);
  useEffect(() => { setTo(defaultTo); }, [defaultTo]);
  const send = async () => {
    setSending(true);
    try {
      const r = await stagingFetch(`${API}/${itemKey}/test`, { method: 'POST', body: JSON.stringify({ to, subject, body }) });
      toast.success(`Test sent to ${r.sent_to}`);
    } catch (e: any) {
      toast.error(e.message || 'Could not send the test');
    } finally {
      setSending(false);
    }
  };
  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : 'mt-1'}`}>
      <Input className="h-9 w-64" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" aria-label="Send the test to" />
      <Button type="button" variant="outline" size="sm" disabled={sending || !to} onClick={send}>{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test</Button>
    </div>
  );
}

function Editor({ item, installed, myEmail, onClose, onSaved }: { item: Item; installed: boolean; myEmail: string; onClose: () => void; onSaved: () => void }) {
  const [subject, setSubject] = useState(item.subject || '');
  const [body, setBody] = useState(item.body);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<any[] | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const check = useMemo(() => checkTemplate(item.key, { subject, body }), [item.key, subject, body]);
  const dirty = subject !== (item.subject || '') || body !== item.body;

  useEffect(() => { stagingFetch(`${API}/${item.key}/history`).then((d) => setHistory(d.versions || [])).catch(() => setHistory([])); }, [item.key]);

  const insert = (name: string) => {
    const el = area.current;
    const token = `{{${name}}}`;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };
  const save = async () => {
    setSaving(true);
    try {
      await stagingFetch(`${API}/${item.key}`, { method: 'PUT', body: JSON.stringify(item.channel === 'email' ? { subject, body } : { body }) });
      toast.success('Saved. The next one that goes out uses this wording.');
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };
  const reset = async () => {
    if (!window.confirm(`Put "${item.name}" back to its original wording?`)) return;
    try {
      await stagingFetch(`${API}/${item.key}/reset`, { method: 'POST' });
      toast.success('Back to the original wording');
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Could not reset');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item.name}</DialogTitle>
          <DialogDescription>{item.audience} · {CHANNEL_LABEL[item.channel]} · {item.when}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-3">
            {item.channel === 'email' && (
              <label className="block text-sm"><span className="font-medium">Subject</span>
                <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </label>
            )}
            <label className="block text-sm"><span className="font-medium">Wording</span>
              <Textarea ref={area} className="mt-1 min-h-[300px] font-mono text-[13px] leading-relaxed" value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
            {item.fields.length > 0 && (
              <div>
                <p className="text-xs font-medium">Fields (click to add; filled in for each person):</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {item.fields.map((f) => <button key={f.name} type="button" onClick={() => insert(f.name)} className="rounded-full border bg-card px-2 py-0.5 text-xs hover:bg-accent" title={`e.g. ${f.sample}`}>{f.label} <span className="text-muted-foreground">{`{{${f.name}}}`}</span></button>)}
                </div>
              </div>
            )}
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Formatting</summary>
              <p className="mt-1">A blank line starts a new paragraph. <code># Title</code> makes a heading, <code>- item</code> a bullet, <code>**words**</code> bold. <code>[Button text](link)</code> on its own line makes a button; inside a sentence it makes a link. The Love &amp; Photos header and footer are added automatically{item.offer ? ', with the "stop offers like this" line' : ''}.</p>
            </details>
            {check.errors.map((e) => <p key={e} className="text-sm text-destructive">{e}</p>)}
            {check.warnings.map((w) => <p key={w} className="text-sm text-amber-700">{w}</p>)}
            {!installed && <p className="text-sm text-amber-700">Saving needs the notification_templates migration. You can still preview and send tests.</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="button" disabled={!dirty || saving || check.errors.length > 0 || !installed} onClick={save}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save wording</Button>
              {item.customized && <Button type="button" variant="outline" onClick={reset}><RotateCcw className="h-4 w-4" /> Back to original</Button>}
              <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-sm font-medium">Send a test of this wording</p>
              <p className="text-xs text-muted-foreground">Uses sample details. The wording on screen is sent, even before you save.</p>
              <SendTest itemKey={item.key} subject={item.channel === 'email' ? subject : undefined} body={body} defaultTo={myEmail} />
            </div>
            <div className="rounded-lg border p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium"><History className="h-4 w-4" /> History</p>
              {history === null ? <p className="text-xs text-muted-foreground">Loading…</p> : history.length === 0 ? <p className="text-xs text-muted-foreground">No changes yet: this is the original wording.</p> : (
                <ul className="mt-1 space-y-1 text-xs">
                  {history.map((v) => (
                    <li key={v.id} className="flex flex-wrap items-center gap-2">
                      <span className="tabular-nums text-muted-foreground">{shortDate(v.changed_at)}</span>
                      <span>{ACTION_LABEL[v.action] || v.action}{v.changed_by_name ? ` by ${v.changed_by_name}` : ''}</span>
                      {v.action === 'edited' && <button type="button" className="underline" onClick={() => { setSubject(v.subject ?? item.default_subject ?? ''); setBody(v.body ?? item.default_body); }}>Load this version</button>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div>
            <p className="mb-1 text-sm font-medium">Preview (sample details)</p>
            <Preview item={item} subject={subject} body={body} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Read-only look at a message as it goes out today (Trera, 27 Sep 2026: "a see preview of each message"). */
function PreviewDialog({ item, myEmail, onClose, onEdit }: { item: Item; myEmail: string; onClose: () => void; onEdit: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item.name}</DialogTitle>
          <DialogDescription>{item.audience} · {CHANNEL_LABEL[item.channel]} · {item.when}{item.enabled ? '' : ' · Currently OFF: this is not being sent'}</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Shown with sample details (a client named Summer, a date in October…); real messages fill in each person&apos;s own.</p>
        <Preview item={item} subject={item.subject || ''} body={item.body} />
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <SendTest itemKey={item.key} defaultTo={myEmail} compact />
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onEdit}><Pencil className="h-4 w-4" /> Edit</Button>
            <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Where vendor follow-up replies go (Traci's Loom, 28 Sep 2026: "when they respond, it'll send to our business email
 * and phone number, which populates into Go High Level"). Each channel holds while its box is empty.
 */
function VendorSettings() {
  const [s, setS] = useState<{ installed: boolean; reply_email: string; sms_from: string; channels: string[]; sent_30d: number; failed_30d: number } | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const load = () => stagingFetch('/api/admin/staging/notifications/vendor-settings').then((d) => { setS(d); setEmail(d.reply_email || ''); setPhone(d.sms_from || ''); }).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  if (!s || !Array.isArray(s.channels)) return null;
  const save = async () => {
    setSaving(true);
    try {
      await stagingFetch('/api/admin/staging/notifications/vendor-settings', { method: 'PUT', body: JSON.stringify({ reply_email: email, sms_from: phone }) });
      toast.success('Vendor follow-up settings saved');
      load();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };
  const changed = email.trim() !== (s.reply_email || '') || phone.trim() !== (s.sms_from || '');
  return (
    <div className="rounded-xl border bg-card p-4 space-y-2">
      <p className="font-medium">Vendor follow-ups: where replies go</p>
      <p className="text-xs text-muted-foreground">
        Emails to vendors carry this address as Reply-To, and texts come from this number, so replies land in GoHighLevel for sales. Leave a box empty and that channel is held: nothing goes out.
        {!s.installed && ' Needs the 20261102120000 migration first.'}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label="Business email for vendor replies" className="h-9 w-64" placeholder="Business email (GHL)" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input aria-label="GHL phone number for vendor texts" className="h-9 w-48" placeholder="GHL phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Button size="sm" disabled={saving || !changed} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Emails: <b>{s.channels.includes('email') ? 'on' : 'held'}</b> · Texts: <b>{s.channels.includes('sms') ? 'on' : 'held'}</b> · Last 30 days: {s.sent_30d} sent{s.failed_30d ? `, ${s.failed_30d} failed` : ''}
      </p>
    </div>
  );
}

export default function NotificationTemplates() {
  const [data, setData] = useState<{ installed: boolean; my_email: string | null; groups: Group[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Item | null>(null);
  const [previewing, setPreviewing] = useState<Item | null>(null);
  const [testKey, setTestKey] = useState<string>('');
  const load = () => stagingFetch(API).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message || String(e)));
  useEffect(() => { load(); }, []);

  const all = useMemo(() => (data ? data.groups.flatMap((g) => g.sections.flatMap((s) => s.items.map((i) => ({ ...i, groupLabel: g.label })))) : []), [data]);
  useEffect(() => { if (!testKey && all.length) setTestKey(all[0].key); }, [all, testKey]);

  const toggle = async (item: Item, enabled: boolean) => {
    if (!enabled && !window.confirm(`Turn off "${item.name}"? It stops going out to anyone until it is turned back on.`)) return;
    try {
      await stagingFetch(`${API}/${item.key}`, { method: 'PUT', body: JSON.stringify({ enabled }) });
      toast.success(enabled ? `"${item.name}" is on` : `"${item.name}" is off`);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Could not change it');
    }
  };

  if (error) return <p className="text-sm text-destructive">Could not load notifications: {error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const myEmail = data.my_email || '';

  return (
    <div className="space-y-5">
      {!data.installed && <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">Turning messages on or off and saving wording need the notification_templates migration. Previews and test sends work now.</p>}

      <div className="rounded-xl border bg-card p-4">
        <p className="font-medium">Send a test</p>
        <p className="text-xs text-muted-foreground">Pick any message and an inbox to see exactly what it looks like. It goes out with sample details and &ldquo;[Test]&rdquo; in the subject; texts arrive as an email showing the text.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select aria-label="Message to test" className="h-9 max-w-md rounded-md border bg-background px-2 text-sm" value={testKey} onChange={(e) => setTestKey(e.target.value)}>
            {data.groups.map((g) => (
              <optgroup key={g.group} label={g.label}>
                {g.sections.flatMap((s) => s.items).map((i) => <option key={i.key} value={i.key}>{i.name} ({CHANNEL_LABEL[i.channel].toLowerCase()})</option>)}
              </optgroup>
            ))}
          </select>
          {testKey && <SendTest itemKey={testKey} defaultTo={myEmail} compact />}
        </div>
      </div>

      {data.groups.map((g) => (
        <div key={g.group} className="space-y-3">
          <h4 className="text-base font-semibold">{g.label}</h4>
          {g.group === 'vendor' && <VendorSettings />}
          {g.sections.map((s) => (
            <div key={s.section} className="rounded-xl border bg-card">
              <div className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.section}</div>
              <div className="divide-y">
                {s.items.map((i) => (
                  <div key={i.key} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${i.enabled ? '' : 'bg-muted/40'}`}>
                    <span className="text-muted-foreground" title={CHANNEL_LABEL[i.channel]}>{i.channel === 'email' ? <Mail className="h-4 w-4" /> : i.channel === 'chat' ? <MessagesSquare className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}</span>
                    <div className="min-w-[220px] flex-1">
                      <div className="text-sm font-medium">{i.name}{i.offer && <span className="ml-2 rounded-full border px-1.5 text-[10px] uppercase text-muted-foreground">offer</span>}</div>
                      <div className="text-xs text-muted-foreground">{i.audience} · {i.when}</div>
                      {i.customized && <div className="text-xs text-primary">Edited{i.updated_by_name ? ` by ${i.updated_by_name}` : ''}{i.updated_at ? ` on ${shortDate(i.updated_at)}` : ''}</div>}
                    </div>
                    <span className="w-28 text-right text-xs tabular-nums text-muted-foreground" title={i.sent_note || undefined}>{i.sent_30d === null ? '' : `${i.sent_30d} sent (30 days)`}</span>
                    <label className="flex items-center gap-2 text-xs">
                      <Switch checked={i.enabled} disabled={!data.installed} onCheckedChange={(v: boolean) => toggle(i, v)} aria-label={`${i.name} on or off`} />
                      <span className={i.enabled ? 'text-emerald-700' : 'text-muted-foreground'}>{i.enabled ? 'On' : 'Off'}</span>
                    </label>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setPreviewing(i)}><Eye className="h-4 w-4" /> Preview</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setEditing(i)}><Pencil className="h-4 w-4" /> Edit</Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {previewing && <PreviewDialog item={previewing} myEmail={myEmail} onClose={() => setPreviewing(null)} onEdit={() => { setEditing(previewing); setPreviewing(null); }} />}
      {editing && <Editor item={editing} installed={data.installed} myEmail={myEmail} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
